import cn from 'classnames';
import * as React from 'react';
import { Link } from 'react-router-dom';

import CroppedContent from 'shared/newComponents/CroppedContent';
import TransComp from 'shared/components/TransComp';
import {
  BaseItemColor,
  BaseItemSize
} from 'shared/entities/components/BaseItem';
import {
  ComponentProps,
  ComponentType
} from 'shared/entities/components/Component';
import WithLabel from 'shared/components/WithLabel';
import { TranslationNode } from 'shared/entities/localization';
import { useUiStore } from 'stores/index';

import './BaseItem.modules.scss';

import { IListItemOptions } from '../types';

export type BaseItemProps = IListItemOptions & {
  className?: string;
  size?: BaseItemSize;
  color?: BaseItemColor;
  limited?: boolean;
  fullWidth?: boolean;
  label?: TranslationNode;
  extraComponent?: React.ReactNode;
} & (
    | ComponentProps
    | {
        type: null;
      }
  );

const BaseItem = React.forwardRef<any, BaseItemProps>(
  (props: BaseItemProps, ref: React.Ref<any>) => {
    const uiStore = useUiStore();
    const {
      title,
      left = null,
      right,
      border,
      hoverable = true,
      isDisabled,
      isSelected,
      size = BaseItemSize.NORMAL,
      className,
      color = BaseItemColor.gray,
      limited = true,
      fullWidth = false,
      titleIcon = null,
      label,
      extraComponent = null
    } = props;

    const styleName = cn(
      'base-item',
      `base-item_${size}`,
      `base-item_${color}`,
      fullWidth && 'base-item_full',
      isSelected && 'base-item_selected',
      isDisabled && 'base-item_disabled',
      hoverable && 'base-item_hoverable'
    );
    const classNameAgr = cn(border && `popup-menu-item_${border}`, className);

    const titleNode = <TransComp>{title}</TransComp>;

    const titleElement = (
      <div
        className="flex-vertical-align"
        styleName="base-item__title-container"
      >
        {titleIcon}
        <CroppedContent
          disabled={uiStore.isMobileDevice}
          popupContent={titleNode}
        >
          <div
            styleName={cn(
              'base-item__title',
              limited && 'base-item__title_limited',
              extraComponent && 'base-item__title_with-extra'
            )}
          >
            {titleNode}
          </div>
        </CroppedContent>
        {extraComponent}
      </div>
    );
    const content = (
      <>
        {left && <div styleName="base-item__icon">{left}</div>}
        <div styleName="base-item__content">
          {label ? (
            <WithLabel label={label}>{titleElement}</WithLabel>
          ) : (
            titleElement
          )}
          {right && <div styleName="base-item__right">{right}</div>}
        </div>
      </>
    );

    if (props.type === ComponentType.link) {
      return (
        <Link
          ref={ref}
          to={props.to}
          target={props.target}
          styleName={styleName}
          className={classNameAgr}
        >
          {content}
        </Link>
      );
    }

    if (props.type === ComponentType.div) {
      return (
        <div
          ref={ref}
          data-testid="base-item"
          onClick={props.onClick}
          styleName={styleName}
          className={classNameAgr}
        >
          {content}
        </div>
      );
    }

    return (
      <div ref={ref} styleName={styleName} className={classNameAgr}>
        {content}
      </div>
    );
  }
);

export default BaseItem;
